import { clerkFetch } from "../api/clerkFetch";

export interface EditRequest {
  provider: "anthropic" | "openai" | "local";
  model: string;
  filePath: string;
  fileContent: string;
  selectionStart: number;
  selectionEnd: number;
  prompt: string;
  localBaseUrl?: string;
  dlpOverride?: boolean;
}

export interface MeterEvent {
  in: number;
  out: number;
  cached: number;
  cacheWrite?: number;
  cents: number;
}

export interface DlpHit {
  label: string;
  tier: "medium";
}

export interface EditStream {
  onDelta: (cb: (chunk: string) => void) => void;
  onDlp: (cb: (hit: DlpHit) => void) => void;
  onUsage: (cb: (usage: MeterEvent) => void) => void;
  done: Promise<MeterEvent>;
  abort: () => void;
}

export function startEditorEdit(req: EditRequest): EditStream {
  const deltaCbs: Array<(chunk: string) => void> = [];
  const dlpCbs: Array<(hit: DlpHit) => void> = [];
  const usageCbs: Array<(usage: MeterEvent) => void> = [];
  const abortCtrl = new AbortController();

  const headers: Record<string, string> = { "content-type": "application/json" };
  const anthKey = localStorage.getItem("agentguard-key-anthropic");
  const openKey = localStorage.getItem("agentguard-key-openai");
  if (anthKey) headers["x-anthropic-api-key"] = anthKey;
  if (openKey) headers["x-openai-api-key"] = openKey;

  const url = `/api/editor/edit?dlpOverride=${req.dlpOverride ? "1" : "0"}`;

  const done: Promise<MeterEvent> = (async () => {
    try {
      const res = await clerkFetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(req),
        signal: abortCtrl.signal,
      });
      if (res.status === 412) {
        const body = await res.json();
        throw body;
      }
      if (!res.ok || !res.body) {
        throw new Error(`Edit request failed: ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalUsage: MeterEvent = { in: 0, out: 0, cached: 0, cents: 0 };
      while (true) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const block of events) {
          if (!block.trim()) continue;
          const lines = block.split("\n");
          const eventLine = lines.find((l) => l.startsWith("event: "));
          const dataLine = lines.find((l) => l.startsWith("data: "));
          if (!eventLine || !dataLine) continue;
          const name = eventLine.slice("event: ".length);
          const data = JSON.parse(dataLine.slice("data: ".length));
          if (name === "delta") deltaCbs.forEach((cb) => cb(data.text));
          else if (name === "dlp") dlpCbs.forEach((cb) => cb(data));
          else if (name === "usage") usageCbs.forEach((cb) => cb(data));
          else if (name === "done") {
            finalUsage = data;
            usageCbs.forEach((cb) => cb(data));
          }
        }
      }
      return finalUsage;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new Error("Edit aborted");
      }
      throw err;
    }
  })();

  return {
    onDelta: (cb) => {
      deltaCbs.push(cb);
    },
    onDlp: (cb) => {
      dlpCbs.push(cb);
    },
    onUsage: (cb) => {
      usageCbs.push(cb);
    },
    done,
    abort: () => abortCtrl.abort(),
  };
}
